import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'

import { request } from '../api/client'

interface AddressBucket {
  value: string
  count: number
  scope: 'external' | 'internal' | 'loopback' | 'unknown'
  on_the_alert: boolean
}

export interface NetworkContextData {
  applicable: boolean
  detail: string
  events: number
  hostname: string
  talked_to: AddressBucket[]
  talked_to_by: AddressBucket[]
  ports: { value: string; count: number }[]
  sources: { value: string; count: number }[]
  truncated: boolean
}

const SCOPE: Record<AddressBucket['scope'], string> = {
  external: 'outside',
  internal: 'ours',
  loopback: 'this host',
  unknown: 'unreadable',
}

/**
 * Who this host was talking to, around the alert.
 *
 * The panel's whole job is to keep two things apart that look identical: a host that
 * talked to nobody, and a host where nothing logs connections. An empty box means the
 * second far more often than the first, so when there is no network telemetry at all
 * the panel says exactly that instead of drawing nothing.
 *
 * It also says what it is made of. A network picture assembled from sshd and nginx
 * access logs is a different claim from one assembled from flow telemetry, and the
 * reader is entitled to know which they are looking at before they conclude that the
 * host only spoke to two addresses.
 */
export function NetworkContext({ id }: { id: string }) {
  const query = useQuery({
    queryKey: ['finding-network', id],
    queryFn: () => request<NetworkContextData>(`/findings/${id}/network/`),
    // Separate from the alert and from the process tree, and not retried: three
    // aggregates over the event store must not take the alert's own story off the
    // screen during the incident that made the store slow.
    retry: false,
  })

  if (query.isError)
    return (
      <p className="muted" style={{ margin: 0 }}>
        The network picture could not be loaded, so nothing here is known — including
        whether this host talked to anything. The alert above is unaffected.
      </p>
    )
  if (!query.data) return <div className="skeleton" />

  const data = query.data
  if (!data.applicable)
    return (
      <p className="muted" style={{ margin: 0 }}>
        {data.detail}
      </p>
    )

  return (
    <>
      <Addresses title="Talked to" rows={data.talked_to} />
      <Addresses title="Talked to by" rows={data.talked_to_by} />

      {data.ports.length > 0 && (
        <p className="muted" style={{ fontSize: 'var(--text-xs)' }}>
          Ports: {data.ports.map((port) => `${port.value} (${port.count})`).join(', ')}
        </p>
      )}

      {data.truncated && (
        <p className="muted" style={{ fontSize: 'var(--text-xs)' }}>
          Top values only — these are not all of them.
        </p>
      )}

      {/* Never omitted. Two addresses out of nginx is not the same statement as two
          addresses out of a flow record, and the difference decides what an empty
          list means. */}
      <p className="muted" style={{ fontSize: 'var(--text-xs)', marginBottom: 0 }}>
        From {data.events.toLocaleString()} event(s) recorded by{' '}
        {data.sources.map((source) => source.value).join(', ') || 'no named parser'}. Anything
        not collected is not shown.{' '}
        <Link to={`/hunt?hostname=${encodeURIComponent(data.hostname)}&group_by=dst_ip`}>
          hunt this host
        </Link>
      </p>
    </>
  )
}

function Addresses({ title, rows }: { title: string; rows: AddressBucket[] }) {
  if (rows.length === 0) return null
  return (
    <>
      <h4 style={{ margin: 'var(--space-2) 0 var(--space-1)', fontSize: 'var(--text-sm)' }}>
        {title}
      </h4>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {rows.map((row) => (
          <li
            key={row.value}
            style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'space-between' }}
          >
            <span className="mono">
              {row.value}
              {/* The scope is a word, not a colour, and "outside" is the word an
                  analyst reads fastest at 03:00. */}
              <span className="muted">{` · ${SCOPE[row.scope]}`}</span>
              {row.on_the_alert && <span className="badge"> on this alert</span>}
            </span>
            <strong>{row.count.toLocaleString()}</strong>
          </li>
        ))}
      </ul>
    </>
  )
}
